// F3/FR-SH5a: the `GET /share/{token}` + transcript load, gate/gone state machine, and the
// uploading/processing poll loop — shared by SharePage and EmbedPage, the two routes that
// resolve a share *token* (as opposed to WatchPage, which resolves a grackle id and only
// optionally carries `?share=`).
import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiFail } from "../api/client";
import type { WatchOut } from "../api/types";
import { getGrackleTranscript } from "../live/client";
import type { GrackleTranscript } from "../live/types";
import { isLinkUnavailable, shareGateFromError, unlockHeaders, type ShareGateInfo } from "./shareGateLogic";

const POLL_MS = 5000;

export interface ShareWatchState {
  watch: WatchOut | null;
  transcript: GrackleTranscript | null;
  notFound: boolean;
  gone: boolean;
  gate: ShareGateInfo | null;
  loadError: string | null;
  unlockToken: string | null;
  reload: () => void;
  onUnlocked: (unlockToken: string) => void;
}

export function useShareWatch(token: string | undefined): ShareWatchState {
  const [watch, setWatch] = useState<WatchOut | null>(null);
  const [transcript, setTranscript] = useState<GrackleTranscript | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [gone, setGone] = useState(false);
  const [gate, setGate] = useState<ShareGateInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Held in memory, not state: carrying it forward must never itself trigger a re-render/
  // re-fetch loop (see the mount effect below, which resets this on a token change only).
  const unlockTokenRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    const headers = unlockHeaders(unlockTokenRef.current);
    try {
      const res = await api<WatchOut>("GET", `/share/${encodeURIComponent(token)}`, undefined, headers);
      if (res.unlock_token) unlockTokenRef.current = res.unlock_token;
      setWatch(res);
      setGate(null);
      setGone(false);
      setLoadError(null);
      if (res.grackle.transcript_status !== "none") {
        try {
          // Re-read: a first full unlock may have just been minted by the call above.
          const t = await getGrackleTranscript(res.grackle.id, token, unlockHeaders(unlockTokenRef.current));
          setTranscript(t);
        } catch {
          setTranscript(null);
        }
      } else {
        setTranscript(null);
      }
    } catch (err) {
      const gateInfo = shareGateFromError(err);
      if (gateInfo) {
        setGate(gateInfo);
        setWatch(null);
        setLoadError(null);
      } else if (isLinkUnavailable(err)) {
        setGone(true);
        setGate(null);
        setWatch(null);
        setLoadError(null);
      } else if (err instanceof ApiFail && err.status === 404) {
        setNotFound(true);
      } else {
        setLoadError(err instanceof ApiFail ? err.message : "Couldn't reach Grackle. Check your connection.");
      }
    }
  }, [token]);

  useEffect(() => {
    unlockTokenRef.current = null;
    setWatch(null);
    setNotFound(false);
    setGone(false);
    setGate(null);
    setLoadError(null);
    void load();
  }, [token, load]);

  useEffect(() => {
    if (!watch) return;
    // Reset PR3: also poll while a transcript is queued/running so the panel fills in.
    const status = watch.grackle.status;
    const transcriptStatus = watch.grackle.transcript_status;
    const shouldPoll =
      status === "uploading" || status === "processing" || transcriptStatus === "queued" || transcriptStatus === "running";
    if (!shouldPoll) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    pollRef.current = setInterval(() => void load(), POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [watch, load]);

  return {
    watch,
    transcript,
    notFound,
    gone,
    gate,
    loadError,
    unlockToken: unlockTokenRef.current,
    reload: () => void load(),
    onUnlocked: (unlockToken: string) => {
      unlockTokenRef.current = unlockToken;
      void load();
    },
  };
}
